/** A serialized, trailing-write queue. A failed write remains retryable. */
export function createOfferSaveQueue(persist, onState = () => {}) {
  let pending = null;
  let running = null;
  let timer;
  const flush = () => {
    clearTimeout(timer);
    if (running) return running;
    if (!pending) return Promise.resolve();
    running = (async () => {
      onState("saving");
      while (pending) {
        const next = pending;
        pending = null;
        try {
          await persist(next);
        } catch (error) {
          pending = pending || next;
          onState("error");
          throw error;
        }
      }
      onState("saved");
    })().finally(() => { running = null; });
    return running;
  };
  return {
    schedule(value, delay = 700) {
      pending = value;
      onState("pending");
      clearTimeout(timer);
      timer = setTimeout(() => { flush().catch(() => {}); }, delay);
    },
    /** Drop queued writes without flushing. In-flight persist still finishes. */
    cancel() {
      clearTimeout(timer);
      pending = null;
      if (!running) onState("idle");
    },
    flush,
    isDirty: () => Boolean(pending || running),
  };
}

const queues = new Map();
export function registerOfferSave(offerId, queue) {
  const key = String(offerId);
  if (!queues.has(key)) queues.set(key, new Set());
  queues.get(key).add(queue);
  return () => {
    const unregister = () => {
      queues.get(key)?.delete(queue);
      if (!queues.get(key)?.size) queues.delete(key);
    };
    // Flush on unmount. Unregister only after a clean flush so failed/dirty
    // work stays visible to beforeunload / navigation guards and retry.
    queue.flush().then(unregister, () => {
      if (!queue.isDirty()) unregister();
    });
  };
}
export async function flushOfferSaves(offerId) {
  await Promise.all([...queues.get(String(offerId)) || []].map((queue) => queue.flush()));
}

export function areOfferSavesDirty(offerId) {
  return [...(queues.get(String(offerId)) || [])].some((queue) => queue.isDirty());
}
